import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SortableTableHeadCell from 'components/common/SortableTableHeadCell';

const styles = theme => ({
  cellHidden: {
    padding: 0,
    fontSize: 0,
  },
  cell: {
    paddingLeft: 4,
    paddingRight: 4,
  },
  cellExpansionActions: {
    width: 60,
    paddingRight: 0,
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    '&:first-child': {
      paddingLeft: 32,
    },
  },
  cellSpecialState: {
    width: 16,
    padding: '0px 8px 0px 8px',
  },
  cellPatient: {
    width: '16%',
  },
  cellPrescriber: {
    width: '12%',
  },
  cellInsurance: {
    width: '16%',
  },
  cellDateOfService: {
    width: 105,
  },
  cellHCPCSCode: {
    width: '10.25%',
  },
  cellHCPCSCodeCollection: {
    width: '8%',
  },
  cellTotalReimbs: {
    width: 74,
  },
  cellTotalCharge: {
    width: 74,
  },
  cellSales: {
    width: '12%',
  },
  cellSalesVariantCombined: {
    width: '18%',
  },
  cellCreationDate: {
    width: 40,
  },
  cellButton: {
    width: 44,
    padding: 0,
    '&:last-child': {
      padding: 0,
    },
  },
  cellTransmission: {
    width: 24,
    padding: 0,
  },
  cellFacility: {
    width: 20,
  },
  cellOrderNumber: {
    width: 44,
    paddingLeft: 0,
  },
});

const OrdersTableHead = ({
  variant,
  sort,
  sortBy,
  onSortingChange,
  loading,
  classes,
}) => (
  <TableHead>
    <TableRow>
      <TableCell className={classNames(classes.cell, classes.cellExpansionActions)}>
        Lines
      </TableCell>
      <TableCell className={classNames(classes.cell, classes.cellSpecialState, classes.cellHidden)}>
        Special State
      </TableCell>
      <TableCell className={classNames(classes.cell, classes.cellOrderNumber)}>
        Order #
      </TableCell>
      {variant !== 'combined' &&
        <TableCell className={classNames(classes.cell, classes.cellPatient)}>
          <SortableTableHeadCell
            label="Patient, DOB"
            sortField="Patient Name"
            sortBy={sortBy}
            sort={sort}
            onSortingChange={onSortingChange}
            loading={loading}
          />
        </TableCell>
      }
      <TableCell
        className={classNames(classes.cell, classes.cellPrescriber)}
      >
        <SortableTableHeadCell
          label="Prescriber"
          sortField="Prescriber Name"
          sortBy={sortBy}
          sort={sort}
          onSortingChange={onSortingChange}
          loading={loading}
        />
      </TableCell>
      <TableCell
        className={classNames(classes.cell, classes.cellInsurance)}
      >
        <SortableTableHeadCell
          label="Insurance"
          sortField="Insurance Name"
          sortBy={sortBy}
          sort={sort}
          onSortingChange={onSortingChange}
          loading={loading}
        />
      </TableCell>
      <TableCell className={classNames(classes.cell, classes.cellDateOfService)}>
        <SortableTableHeadCell
          label="Date of Service"
          sortField="Date of Service From"
          tooltipLabel="Date of Service"
          sortBy={sortBy}
          sort={sort}
          onSortingChange={onSortingChange}
          loading={loading}
        />
      </TableCell>
      <TableCell
        className={classNames(classes.cell, classes.cellHCPCSCode)}
      >
        HCPCS
      </TableCell>
      {['workflow', 'combined'].indexOf(variant) !== -1 &&
        <TableCell className={classNames(classes.cell, classes.cellTotalCharge)}>
          Total Charge
        </TableCell>
      }
      {['workflow', 'combined'].indexOf(variant) !== -1 &&
        <TableCell className={classNames(classes.cell, classes.cellTotalReimbs)}>
          Total Reimbs
        </TableCell>
      }
      <TableCell
        className={classNames(classes.cell, classes.cellSales, {
          [classes.cellSalesVariantCombined]: variant === 'combined',
        })}
      >
        <SortableTableHeadCell
          label="Sales"
          sortField="Sales Representative Name"
          sortBy={sortBy}
          sort={sort}
          onSortingChange={onSortingChange}
          loading={loading}
        />
      </TableCell>
      {['workflow', 'combined'].indexOf(variant) !== -1 &&
        <TableCell className={classNames(classes.cell, classes.cellDateOfService)}>
          <SortableTableHeadCell
            label="Date of Delivery"
            sortField="Date of Delivery"
            sortBy={sortBy}
            sort={sort}
            onSortingChange={onSortingChange}
            loading={loading}
          />
        </TableCell>
      }
      <TableCell className={classNames(classes.cell, classes.cellCreationDate)}>
        <SortableTableHeadCell
          label="Aging"
          sortField="Date of Creation"
          tooltipLabel="Aging Days"
          sortBy={sortBy}
          sort={sort}
          onSortingChange={onSortingChange}
          loading={loading}
          invertedSort
        />
      </TableCell>
      <TableCell className={classNames(classes.cell,
        classes.cellHidden, classes.cellFacility)}
      >
        Facility
      </TableCell>
      <TableCell className={classNames(classes.cell,
        classes.cellHidden, classes.cellTransmission)}
      >
        Transmission Method
      </TableCell>
      <TableCell className={classNames(classes.cell,
        classes.cellHidden, classes.cellButton)}
      >
        Messages
      </TableCell>
      {variant === 'workflow' &&
        <Fragment>
          <TableCell className={classNames(classes.cell,
            classes.cellHidden, classes.cellButton)}
          >
            SMS
          </TableCell>
          <TableCell className={classNames(classes.cell,
            classes.cellHidden, classes.cellButton)}
          >
            Watchers
          </TableCell>
        </Fragment>
      }
      <TableCell className={classNames(classes.cell,
        classes.cellHidden, classes.cellButton)}
      >
        Actions
      </TableCell>
    </TableRow>
  </TableHead>
);

OrdersTableHead.propTypes = {
  variant: PropTypes.oneOf([
    'workflow',
    'combined',
    'connected',
  ]).isRequired,
  sort: PropTypes.oneOf(['asc', 'desc']),
  sortBy: PropTypes.string,
  onSortingChange: PropTypes.func,
  loading: PropTypes.number,
  classes: PropTypes.shape({
    cell: PropTypes.string.isRequired,
    cellHidden: PropTypes.string.isRequired,
    cellDateOfService: PropTypes.string.isRequired,
    cellHCPCSCode: PropTypes.string.isRequired,
    cellHCPCSCodeCollection: PropTypes.string.isRequired,
    cellTotalReimbs: PropTypes.string.isRequired,
    cellTotalCharge: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersTableHead.defaultProps = {
  sort: null,
  sortBy: null,
  onSortingChange: null,
  loading: 0,
};

export default withStyles(styles)(OrdersTableHead);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersTable/OrdersTableHead.js